import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';

import { Order, OrderSap } from '../../api-client';
import { RootState } from '../../application/store';
import { AdminApiClient, OrderApiClient } from '../../providers/api-provider';
import { upsertOrder } from '../../slices/OrderSummary/orderSummarySlice';
import { clearQuotaAvailability } from '../../slices/Quota/quotaSlice';
import { ORDER_STATUS } from '../../utils/constants/order-status';
import { STORAGE_KEYS } from '../../utils/constants/storage-keys';
import { OrderConflictDto } from '../../api-client';
import { clearLimitedEditions } from '../../slices/LimitedEdition/limitedEditionSlice';
import { limitedEditionErrorsSummary } from '../../utils/limited-edition-utils';
import { quotaErrorsSummary } from '../../utils/quota-utils';
import { sessionStorageHelper } from '../../utils/storage-helpers/session-storage-helper';
import { useQuotaAvailability } from './useQuotaAvailability';

export const useOrder = () => {
	const { instance } = useMsal();

	const bvCustomer = useSelector((state: RootState) => state.customer);

	const { handleQuotaAvailability } = useQuotaAvailability();

	const dispatch = useDispatch();

	const autosaveOrderForCurrentCustomer = () =>
		// Auto save on every order change
		handleUpsertOrder(
			sessionStorageHelper.getStorageItem(STORAGE_KEYS.BV_ORDER_SUMMARY),
			(orderId) => handleRetrieveOrder(orderId),
			false,
		);

	const resetOrderForCurrentCustomer = () => {
		// The order lifecycle just changed, so the cached quota availability is stale: a confirmed order starts
		// consuming quota, a deleted one gives it back. Clearing it makes the next read fetch fresh numbers.
		dispatch(clearQuotaAvailability());

		dispatch(
			upsertOrder({
				id: undefined,
				status: ORDER_STATUS.DRAFT,
				amount: 0,
				createdAt: new Date().toISOString(),
				customer: {
					id: bvCustomer.id,
					sapCode: bvCustomer.sapCode,
					salesGroupCode: bvCustomer.salesGroupCode,
					fullName: bvCustomer.fullName,
				},
				loginNameSalesAgent: undefined,
				note: '',
				poNumber: '',
				reason: undefined,
				products: [],
			}),
		);
	};

	// CRUD Operations

	const [retrieveOrderLoading, setRetrieveOrderLoading] =
		useState<boolean>(false);

	const handleRetrieveOrder = async (
		id: number = -1,
		callback?: () => void,
	) => {
		setRetrieveOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.getOrder(id)
			.then((response) => {
				dispatch(upsertOrder(response.data));

				callback && callback();
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to retrieve order!',
					description: `Unable to retrieve the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setRetrieveOrderLoading(false);
			});
	};

	const [retrieveOrderSap, setRetrieveOrderSap] = useState<OrderSap[]>([]);
	const [retrieveOrderSapLoading, setRetrieveOrderSapLoading] =
		useState<boolean>(false);

	const handleRetrieveOrderSap = async (id: number) => {
		setRetrieveOrderSapLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.getOrderSap(id)
			.then((response) => {
				setRetrieveOrderSap(response.data);
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to retrieve order sap!',
					description: `Unable to retrieve the order sap for the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setRetrieveOrderSapLoading(false);
			});
	};

	const [upsertOrderLoading, setUpsertOrderLoading] = useState<boolean>(false);

	const handleUpsertOrder = async (
		order: Order,
		callback?: (orderId: number) => void,
		showNotification = true,
	) => {
		setUpsertOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.upsertOrderDraft(order)
			.then((response) => {
				callback && callback(response.data);

				showNotification &&
					notification.success({
						message: 'Order saved!',
						description: 'Order successfully saved.',
						duration: 2,
					});
			})
			.catch((reason) => {
				// A draft holds its limited edition numbers, so saving can be refused: somebody else took one
				// of them in the meantime. Nothing was written, and the pool on screen is stale.
				if (reason.response?.status === 409) {
					const conflict: OrderConflictDto = reason.response.data ?? {};

					dispatch(clearLimitedEditions());

					notification.warning({
						message: 'Limited edition numbers no longer available!',
						description: `The order was not saved. ${limitedEditionErrorsSummary(
							conflict.limitedEditionErrors,
						)} Pick the numbers again.`,
						duration: 6,
					});

					return;
				}

				showNotification &&
					notification.error({
						message: 'Unable to save order!',
						description: `Unable to save the order.`,
						duration: 2,
					});
			})
			.finally(() => {
				setUpsertOrderLoading(false);
			});
	};

	const [finalizeOrderLoading, setFinalizeOrderLoading] =
		useState<boolean>(false);

	const handleFinalizeOrder = async (
		orderId: number,
		onSuccess: () => void,
		onError: () => void,
	) => {
		setFinalizeOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.finalizeOrder(orderId)
			.then((response) => {
				resetOrderForCurrentCustomer();

				onSuccess();

				notification.success({
					message: 'Order finalized!',
					description: 'Order successfully finalized.',
					duration: 2,
				});
			})
			.catch((reason) => {
				if (reason.response && reason.response.status === 409) {
					// Quota exceeded, or a limited edition number no longer usable. The backend changed
					// nothing, so the cart has to stay exactly as it is, block included.
					//
					// The availability is reloaded and not cleared. Clearing would empty the violations, and
					// with them the banner, the row highlight and the disabled Checkout, leaving a cart that
					// looks fine while it is not: nothing would bring them back, because the effect that
					// loads the availability depends on customer and order id and on a conflict neither of
					// the two changes. Keeping the old numbers while the call is in flight avoids showing an
					// unblocked cart for a few frames. force is required: the cache is still warm for this
					// customer, so without it the hook would skip the call.
					const conflict: OrderConflictDto = reason.response.data ?? {};

					void handleQuotaAvailability(
						bvCustomer.id ? Number(bvCustomer.id) : undefined,
						orderId,
						true,
					);

					// The limited edition pool is cleared instead of reloaded: it is fetched per product when the
					// form opens, and an unknown pool keeps the form conservative rather than unblocking it.
					dispatch(clearLimitedEditions());

					onError();

					notification.warning(
						conflict.limitedEditionErrors?.length
							? {
									message: 'Limited edition numbers no longer available!',
									description: `Order not confirmed. ${limitedEditionErrorsSummary(
										conflict.limitedEditionErrors,
									)}`,
									duration: 6,
								}
							: {
									message: 'Quota exceeded!',
									description: `Order not confirmed, the available quota is not enough. ${quotaErrorsSummary(
										conflict.quotaErrors ?? [],
									)}`,
									duration: 4,
								},
					);
				} else if (reason.response && reason.response.status === 412) {
					dispatch(upsertOrder(reason.response.data));

					onError();

					notification.warning({
						message: 'Unable to finalize order!',
						description: `Unable to finalize the order, cause some products are unavailable. Current order is now successfully updated and ready to be completed.`,
						duration: 2,
					});
				} else {
					notification.error({
						message: 'Unable to finalize order!',
						description: `Unable to finalize the order.`,
						duration: 2,
					});
				}
			})
			.finally(() => {
				setFinalizeOrderLoading(false);
			});
	};

	const [deleteOrderLoading, setDeleteOrderLoading] = useState<boolean>(false);

	const handleDeleteOrder = async (id: number = -1, callback?: () => void) => {
		setDeleteOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.deleteOrder(id)
			.then(() => {
				resetOrderForCurrentCustomer();

				callback && callback();

				notification.success({
					message: 'Order deleted!',
					description: `Successfully delete the order #${id}.`,
					duration: 2,
				});
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to delete order!',
					description: `Unable to delete the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setDeleteOrderLoading(false);
			});
	};

	const [exportOrderLoading, setExportOrderLoading] = useState<boolean>(false);

	const handleExportOrder = async (customerName: string, id: number = -1) => {
		setExportOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.printOrder(id, { responseType: 'blob' })
			.then((response) => {
				saveAs(
					new Blob([response.data]),
					`${customerName.replace(/\s+/g, '')}-${new Date().toISOString().split('T')[0].replace(/-+/g, '')}-ORDER-${id}.pdf`,
				);
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to export order!',
					description: `Unable to export the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setExportOrderLoading(false);
			});
	};

	const [repriceOrderLoading, setRepriceOrderLoading] =
		useState<boolean>(false);

	const handleRepriceOrder = async (id: number = -1, callback?: () => void) => {
		setRepriceOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.repriceOrder(id)
			.then((response) => {
				const isComplete = response.data && response.data.complete;
				const isPriceChanged = response.data && response.data.pricesIsChanged;
				const isPriceMissing = response.data && response.data.pricesMissing;

				if (isComplete) {
					handleRetrieveOrder(id);
					callback && callback();

					if (isPriceChanged) {
						if (isPriceMissing) {
							notification.warning({
								message: 'Warning - only some items price was update',
								description: `Partially reprice the order #${id}.`,
								duration: 2,
							});
						} else {
							notification.success({
								message: 'Repricing done successfully',
								description: `Successfully reprice the order #${id}.`,
								duration: 2,
							});
						}
					} else {
						notification.warning({
							message:
								'Warning - items price wasnt change' +
								(isPriceMissing ? ' and price still missing' : ''),
							description: `Reprice the order #${id} was done without variation.`,
							duration: 2,
						});
					}
				} else {
					notification.error({
						message: 'Unable to reprice order!',
						description: `Unable to reprice the order #${id}.`,
						duration: 2,
					});
				}
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to reprice order!',
					description: `Unable to reprice the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setRepriceOrderLoading(false);
			});
	};

	// =========================
	// Admin area
	// =========================

	const [adminOrders, setAdminOrders] = useState<any[] | undefined>(undefined);
	const [adminOrdersLoading, setAdminOrdersLoading] = useState<boolean>(false);

	const handleAdminOrdersByReasonCode = async (reasonCode: string) => {
		setAdminOrdersLoading(true);

		try {
			const adminApi = await AdminApiClient(instance);
			const response = await adminApi.getOrdersByReasonCode(reasonCode);
			setAdminOrders(response.data);
		} catch (err) {
			setAdminOrders([]);

			notification.error({
				message: 'Unable to retrieve products!',
				description: 'Unable to retrieve products.',
				duration: 2,
			});
		} finally {
			setAdminOrdersLoading(false);
		}
	};

	const [exportAdminOrdersLoading, setExportAdminOrdersLoading] =
		useState<boolean>(false);

	const handleExportAdminOrdersByReasonCode = async (reasonCode: string) => {
		setExportAdminOrdersLoading(true);

		try {
			const adminApi = await AdminApiClient(instance);

			const response = await adminApi.exportOrdersByReasonCode(reasonCode, {
				responseType: 'blob',
			});

			const cd =
				response.headers?.['content-disposition'] ||
				response.headers?.['Content-Disposition'];

			let filename = `orders_export_${reasonCode}_${
				new Date().toISOString().split('T')[0]
			}.xlsx`;

			const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
				cd || '',
			);
			if (match) {
				filename = decodeURIComponent(match[1] || match[2]);
			}

			saveAs(new Blob([response.data]), filename);
		} catch (err) {
			notification.error({
				message: 'Unable to export orders!',
				description: 'Unable to export orders.',
				duration: 2,
			});
		} finally {
			setExportAdminOrdersLoading(false);
		}
	};

	return {
		autosaveOrderForCurrentCustomer,
		resetOrderForCurrentCustomer,
		// CRUD Operations
		handleRetrieveOrder,
		retrieveOrderLoading,
		handleRetrieveOrderSap,
		retrieveOrderSap,
		retrieveOrderSapLoading,
		handleUpsertOrder,
		upsertOrderLoading,
		handleFinalizeOrder,
		finalizeOrderLoading,
		handleDeleteOrder,
		deleteOrderLoading,
		handleExportOrder,
		exportOrderLoading,
		handleRepriceOrder,
		repriceOrderLoading,

		adminOrders,
		adminOrdersLoading,
		handleAdminOrdersByReasonCode,
		exportAdminOrdersLoading,
		handleExportAdminOrdersByReasonCode,
	};
};
